<script lang="ts">
  import type { OcDropdownV1Events } from "@otto-ec/otto-components/dropdown";
  import { Filter } from "../filter/Filter";
  import { any } from "../util/Reductions";
  import { type TrackingLabels } from "../tracking/TrackingLabels";
  import { listFeatures, submitActionTrackingFrom } from "../tracking/FeatureTracking";
  import { FacetGroup } from "../filter/FacetGroup";
  import { onMount } from "svelte";
  import { getFacetGroupSelection, setFacetGroupSelection } from "./facetGroupStore";
  import { untrack } from "svelte";

  interface FacetGroupOptions {
    name?: string;
    title?: string;
    selected?: boolean;
    hidden?: boolean;
  }

  interface Props {
    facetGroups?: FacetGroupOptions[];
    label?: string;
    name?: string;
    filterId?: string;
  }

  let { facetGroups = [], label, name, filterId }: Props = $props();
  /*                                             */
  /*                                                                                                                              */
  /*                                                                                             */
  const filter = untrack(() => Filter.filterId(filterId));
  const disabled = untrack(() => facetGroups.filter((facetGroup) => !facetGroup.hidden).length <= 1);

  /*                                               */
  const preselectedFacetGroupName = untrack(() => getFacetGroupSelection(filterId));
  const preselectedFacetGroup = untrack(() =>
    preselectedFacetGroupName
      ? facetGroups.find((fg) => fg.name === preselectedFacetGroupName && !fg.hidden && !fg.selected)
      : undefined,
  );
  if (preselectedFacetGroup) {
    /*                                                                  */
    untrack(() => {
      facetGroups.forEach((fg) => (fg.selected = false));
      preselectedFacetGroup.selected = true;
    });
  }

  /*                                          */
  const selectedFacetGroup = untrack(() => facetGroups.find((fg) => fg.selected));
  const isAnyFacetGroupActive = filter?.facetGroups?.some((fg) => fg.active);
  if (selectedFacetGroup?.hidden || !selectedFacetGroup || !isAnyFacetGroupActive) {
    const firstNonHiddenFacetGroup = untrack(() => facetGroups.find((fg) => !fg.hidden));
    if (firstNonHiddenFacetGroup) {
      firstNonHiddenFacetGroup.selected = true;
    }
  }

  const initialFacetGroupName = untrack(() => facetGroups.find((fg) => fg.selected)?.name);
  activateFacetGroup(initialFacetGroupName);

  let dropdown: HTMLElement;

  /*                                           */
  function activateFacetGroup(facetGroupName: string | undefined) {
    if (facetGroupName) {
      filter?.facetGroups.forEach((facetGroup) => facetGroup.toggleVisibility(facetGroup.hasName(facetGroupName)));
    }
  }

  function activateInitialFacetGroup() {
    if (initialFacetGroupName) {
      activateFacetGroup(initialFacetGroupName);
      if (dropdown && "value" in dropdown) {
        dropdown.value = initialFacetGroupName;
      }
    }
  }

  function onchange(event: OcDropdownV1Events["oc-property-change"]) {
    if (event?.target?.form && event?.detail?.value?.current) {
      const currentFacetGroup = event.detail.value.current;
      const targetFilter = Filter.factory.declare(event.target.form);
      if (targetFilter) {
        const targetFacetGroups = targetFilter.facetGroups;
        const updated = targetFacetGroups.length
          ? targetFacetGroups
              .map((facetGroup) => facetGroup.toggleVisibility(facetGroup.hasName(currentFacetGroup)))
              .reduce(any)
          : false;
        if (updated) {
          setFacetGroupSelection(filterId, currentFacetGroup);
          submitTracking(currentFacetGroup, targetFacetGroups, targetFilter.form.id, initialFacetGroupName);
        }
      }
    }
  }

  function submitTracking(
    facetGroup: string,
    facetGroups: FacetGroup[],
    filterId: string,
    initialFacetGroup: string | undefined,
  ) {
    const labels: Partial<TrackingLabels> = {
      san_DefaultFacetChange: facetGroup,
      san_DefaultFacet: initialFacetGroup || "none",
    };
    facetGroups.forEach((group) => {
      if (group.active) {
        group.updateChildrenFeatureStatus("loaded", "visible");
        submitActionTrackingFrom(group.facetGroup, "open", labels, [
          ...listFeatures(["Filter-Overview"]),
          ...listFeatures(["Filter"], undefined, `[id="${filterId}"]`),
        ]);
      } else {
        group.updateChildrenFeatureStatus("visible", "loaded");
      }
    });
  }

  onMount(() => {
    const form = filter?.form;
    form?.addEventListener("reset", activateInitialFacetGroup, { passive: true });
    return () => form?.removeEventListener("reset", activateInitialFacetGroup);
  });
</script>

<div class="hc_facetGroup">
  <oc-dropdown-v1
    id={filterId}
    class="hc_facetGroup--dropdown"
    oc-aria-label={label}
    {label}
    {name}
    value={initialFacetGroupName}
    {disabled}
    onoc-property-change={onchange}
    bind:this={dropdown}
  >
    {#each facetGroups as facetGroup (facetGroup.title)}
      <option
        value={facetGroup.name}
        selected={facetGroup.selected}
        disabled={facetGroup.hidden}
        hidden={facetGroup.hidden}>{facetGroup.title}</option
      >
    {/each}
  </oc-dropdown-v1>
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .hc_facetGroup {
    margin: calc(var(--oc-semantic-spacing-12) + var(--oc-semantic-spacing-25)) tokens.$oc-semantic-spacing-12
      tokens.$oc-semantic-spacing-150;
  }
</style>
